import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import HeaderPublico from "../../components/HeaderPublico";
import FooterPublico from "../../components/FooterPublico";
import ConsentCheckbox from "../../components/ConsentCheckbox";
import { API_URL, TENANT_ID } from "../../config";
import { persistCheckoutAuthToken } from "../../utils/checkoutSession";
import { clearCurrentUserProfileCache } from "../../utils/profileCache";
import {
  getBirthDateValidationError,
  getMaxBirthDateIso,
  getMinBirthDateIso,
  isValidBirthDate,
} from "../../utils/birthDateValidation";
import {
  IDENTITY_DOCUMENT_FORMAT_MESSAGE,
  IDENTITY_DOCUMENT_INPUT_PATTERN,
  isValidIdentityDocument,
  normalizeIdentityDocumentInput,
} from "../../utils/identityDocument";
import { scrollIntoViewSmooth } from "../../utils/scrollIntoViewSmooth";
import {
  clearRegistrationFormDraft,
  filterRegistrationFormCourseIds,
  loadRegistrationFormDraft,
  saveRegistrationFormDraft,
} from "../../utils/registrationFormDraft";
import {
  mergeRegistrationCoursesWithCart,
  readCartCourseIds,
} from "../../utils/cartCourseSelection";
import { buildCourseDescriptionSnippet } from "../../utils/courseDescriptionSnippet";
import { getCourseCheckoutPrice } from "../../utils/coursePrice";
import { validateBillingCity, validateBillingPostalCode } from "../../utils/validateBillingLocationFields";
import CourseImageFill from "../../components/CourseImageFill";
import "./NewRegistro.css";

const COURSE_THUMB_FALLBACK_SRC =
  "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='80' height='60'><rect width='100%' height='100%' fill='%23eee'/><text x='50%' y='50%' dominant-baseline='middle' text-anchor='middle' font-family='sans-serif' font-size='12' fill='%23999'>Sin imagen</text></svg>";

export default function NewRegistro() {
  const navigate = useNavigate();

  const [cursos, setCursos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState("");
  const [errorScrollTick, setErrorScrollTick] = useState(0);
  const errorRef = useRef(null);

  const [form, setForm] = useState(() => loadRegistrationFormDraft());

  // ▼ Validaciones
  const emailValido = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);
  const dniValido   = (d) => isValidIdentityDocument(d);
  const telValido   = (t) => /^\d{9}$/.test(String(t || "").trim());
  const fechaNacimientoValida = (value) => isValidBirthDate(value);

const [sending, setSending] = useState(false);
  const [submittedOnce, setSubmittedOnce] = useState(false);

  useEffect(() => {
    saveRegistrationFormDraft(form);
  }, [form]);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_URL}/cursos-publicos`);
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || "No se pudieron cargar los cursos");
        const cursosArray = Array.isArray(data)
          ? data
          : Array.isArray(data?.cursos)
            ? data.cursos
            : [];
        setCursos(cursosArray.filter((c) => c && c.disponibleCompra !== false));
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (loading) {
      return;
    }

    const availableCourseIds = cursos.map((curso) => curso._id);
    const cartCourseIds = readCartCourseIds();
    setForm((prev) => {
      const mergedCursos = mergeRegistrationCoursesWithCart(
        prev.cursos,
        cartCourseIds,
        availableCourseIds,
      );
      if (
        mergedCursos.length === prev.cursos.length
        && mergedCursos.every((id, index) => id === prev.cursos[index])
      ) {
        return prev;
      }

      return { ...prev, cursos: mergedCursos };
    });
  }, [cursos, loading]);

  const onChange = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  // ▼ Normalizadores de entrada (evitan teclear inválidos)
  const onChangeDni = (raw) => {
    onChange("dni", normalizeIdentityDocumentInput(raw));
  };

  const showError = (message) => {
    setErrorScrollTick((tick) => tick + 1);
    setError(message);
  };

  useEffect(() => {
    if (errorScrollTick === 0 || !error) {
      return;
    }
    scrollIntoViewSmooth(errorRef.current);
  }, [error, errorScrollTick]);

  const onChangeTelefono = (raw) => {
    const onlyNums = String(raw || "").replace(/\D/g, "").slice(0, 9);
    onChange("telefono", onlyNums);
  };

  const onChangeEmail = (raw) => {
    onChange("email", (raw || "").trim().toLowerCase());
  };

  const onChangeCodigoPostal = (raw) => {
    onChange("codigoPostal", String(raw || "").replace(/\D/g, "").slice(0, 5));
  };

  const toggleCurso = (id) => {
    setForm(prev => {
      const s = new Set(prev.cursos);
      s.has(id) ? s.delete(id) : s.add(id);
      return { ...prev, cursos: Array.from(s) };
    });
  };

  // Únicos por _id
  const cursosUnicos = useMemo(() => {
    const map = new Map(cursos.map(c => [c._id, c]));
    return Array.from(map.values());
  }, [cursos]);

  const selectedValidCursos = useMemo(
    () => filterRegistrationFormCourseIds(form.cursos, cursosUnicos.map((curso) => curso._id)),
    [form.cursos, cursosUnicos],
  );

  const onSubmit = async (e) => {
  e.preventDefault();
  setError("");
  setSubmittedOnce(true);

  if (!emailValido(form.email)) return showError("Revisa el email.");
  if (!dniValido(form.dni))     return showError(IDENTITY_DOCUMENT_FORMAT_MESSAGE);
  if (!fechaNacimientoValida(form.fechaNacimiento)) {
    return showError(getBirthDateValidationError(form.fechaNacimiento) || "Revisa la fecha de nacimiento.");
  }
  if (!telValido(form.telefono)) return showError("Revisa el teléfono (9 dígitos).");
  const cityValidation = validateBillingCity(form.ciudad);
  if (!cityValidation.valid) return showError(cityValidation.error);
  const postalValidation = validateBillingPostalCode(form.codigoPostal);
  if (!postalValidation.valid) return showError(postalValidation.error);
  if (selectedValidCursos.length === 0) return showError("Selecciona al menos un curso.");
  if (!form.consentAccepted) return showError("Debes aceptar los términos y la política de privacidad.");

  try {
    setSending(true);
    // Invalidate any previous browser session so checkout cookies/tokens belong
    // to the newly registered buyer (avoids ORDER_NOT_OWNED / Unauthorized).
    try {
      await fetch(`${API_URL}/logout`, {
        method: "POST",
        credentials: "include",
      });
    } catch {
      /* logout best-effort; registration still proceeds */
    }
    clearCurrentUserProfileCache();
    const payload = {
      ...form,
      cursos: selectedValidCursos,
      ciudad: cityValidation.normalized,
      codigoPostal: postalValidation.normalized,
      telefono: String(form.telefono || "").replace(/\D/g, "").slice(0, 9),
    };
    const res = await fetch(`${API_URL}/registro-publico`, {
      method: "POST",
      headers: { 
        "Content-Type": "application/json",
        "x-tenant-id": TENANT_ID 
      },
      credentials: "include",
      body: JSON.stringify(payload),
    });
    if (res.status === 429) {
      const retry = res.headers.get("Retry-After");
      const seconds = retry ? parseInt(retry, 10) : NaN;
      let msg = "Demasiados intentos. Intenta de nuevo más tarde.";
      if (!Number.isNaN(seconds)) {
        const mins = Math.ceil(seconds / 60);
        msg = `Demasiados intentos. Vuelve a intentar en ${mins} min.`;
      }
      showError(msg);
      return;
    }
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || "Error al registrar");

    if (data.gratis) {
      clearRegistrationFormDraft();
      try {
        localStorage.removeItem("idUsuario");
      } catch {
        /* stale session id must not block navigation */
      }
      navigate("/check-email", { state: { email: form.email } });
      return;
    }
    if (data.pedidoId) {
      try {
        localStorage.removeItem("idUsuario");
      } catch {
        /* stale session id must not block navigation */
      }
      persistCheckoutAuthToken(data.token);
      clearCurrentUserProfileCache();
      navigate(`/checkout?orderId=${encodeURIComponent(data.pedidoId)}`);
      return;
    }
    

    throw new Error("Respuesta inesperada del servidor");
  } catch (e) {
    showError(e.message);
  } finally {
    setSending(false);
  }
};


  const cityField = validateBillingCity(form.ciudad);
  const postalField = validateBillingPostalCode(form.codigoPostal);

  if (loading) return <p className="nr-center">Cargando…</p>;



  return (
    <div className="pagina-publicitaria new-registro-page">
      <HeaderPublico cursos={cursosUnicos} />

      <div className="new-registro">
        <h1>Registro</h1>
        {error && <p ref={errorRef} className="nr-error" role="alert">{error}</p>}

        <form onSubmit={onSubmit} className="nr-form nr-form--pro">
         
          {/* Campos anchos */}
          <input type="text"placeholder="Nombre" value={form.nombre} onChange={e=>onChange("nombre", e.target.value)} required />
          <input type="text"placeholder="Primer Apellido" value={form.primerApellido} onChange={e=>onChange("primerApellido", e.target.value)} required />
          <input type="text"placeholder="Segundo Apellido" value={form.segundoApellido} onChange={e=>onChange("segundoApellido", e.target.value)} required />
          
          {/* Fila: DNI + Fecha + telefono */}
          <div className="nr-row-2">
            <div className="nr-field-wrap">
              <input
                type="text"
                placeholder="DNI o NIE"
                aria-label="DNI o NIE"
                value={form.dni}
                onChange={e=>onChangeDni(e.target.value)}
                inputMode="text"
                autoComplete="off"
                maxLength={9}
                pattern={IDENTITY_DOCUMENT_INPUT_PATTERN}
                title={IDENTITY_DOCUMENT_FORMAT_MESSAGE}
                required
                aria-invalid={submittedOnce && !dniValido(form.dni)}
              />
              {submittedOnce && !dniValido(form.dni) && (
                <span className="nr-field-error">{IDENTITY_DOCUMENT_FORMAT_MESSAGE}</span>
              )}
            </div>
            <div className="nr-field-wrap">
              <input
                type="date"
                value={form.fechaNacimiento}
                onChange={e=>onChange("fechaNacimiento", e.target.value)}
                min={getMinBirthDateIso()}
                max={getMaxBirthDateIso()}
                required
                aria-invalid={submittedOnce && !fechaNacimientoValida(form.fechaNacimiento)}
              />
              {submittedOnce && !fechaNacimientoValida(form.fechaNacimiento) && (
                <span className="nr-field-error">
                  {getBirthDateValidationError(form.fechaNacimiento) || "Revisa la fecha de nacimiento."}
                </span>
              )}
            </div>
            <div className="nr-field-wrap">
              <input
                type="tel"
                placeholder="Teléfono (9 dígitos)"
                value={form.telefono}
                onChange={e=>onChangeTelefono(e.target.value)}
                inputMode="numeric"
                autoComplete="tel"
                maxLength={9}
                pattern="[0-9]{9}"
                title="Número de teléfono: exactamente 9 dígitos"
                required
                aria-invalid={submittedOnce && !telValido(form.telefono)}
              />
              {submittedOnce && !telValido(form.telefono)  && (
                <span className="nr-field-error">El teléfono debe tener exactamente 9 dígitos.</span>
              )}
            </div>
          </div>

          {/* Fila: direccion + Email */}
          <div className="nr-row-3">
            <div className="nr-field-wrap">
              <input
                type="email"
                placeholder="Email"
                value={form.email}
                onChange={e=>onChangeEmail(e.target.value)}
                autoComplete="email"
                required
                aria-invalid={submittedOnce && !emailValido(form.email)}
              />
              {submittedOnce && !emailValido(form.email) && (
                <span className="nr-field-error">Email no válido</span>
              )}
            </div>
           <input 
           type="text"
           placeholder="Dirección"
            value={form.direccion}
            onChange={e=>onChange("direccion", e.target.value)} 
            />

          </div>
          <div className="nr-row-location">
            <div className="nr-field-wrap">
              <input
                type="text"
                placeholder="Ciudad"
                value={form.ciudad}
                onChange={(e) => onChange("ciudad", e.target.value)}
                autoComplete="address-level2"
                required
                aria-invalid={submittedOnce && !cityField.valid}
              />
              {submittedOnce && !cityField.valid && (
                <span className="nr-field-error">{cityField.error}</span>
              )}
            </div>
            <div className="nr-field-wrap">
              <input
                type="text"
                placeholder="Código postal"
                value={form.codigoPostal}
                onChange={(e) => onChangeCodigoPostal(e.target.value)}
                inputMode="numeric"
                autoComplete="postal-code"
                maxLength={5}
                required
                aria-invalid={submittedOnce && !postalField.valid}
              />
              {submittedOnce && !postalField.valid && (
                <span className="nr-field-error">{postalField.error}</span>
              )}
            </div>
          </div>


          {/* Cursos con miniatura */}
          <div className="nr-cursos">
            <div>
              <br></br>
            <h3>Selecciona curso/s</h3>
            {submittedOnce && selectedValidCursos.length === 0 && (
              <span className="nr-field-error">Selecciona al menos un curso</span>
            )}
            <br></br>
            </div>
            <div className="nr-curso-grid nr-curso-grid--scroll">
              {cursosUnicos.map((c) => {
                const descSnippet = buildCourseDescriptionSnippet(c.descripcion);
                return (
                <label key={c._id} className="nr-curso-item">
                  <input
                    type="checkbox"
                    checked={form.cursos.includes(c._id)}
                    onChange={() => toggleCurso(c._id)}
                  />
                  <div className="nr-curso-thumb">
                    <CourseImageFill
                      src={c.imagen || ""}
                      alt={c.titulo}
                      fallbackSrc={COURSE_THUMB_FALLBACK_SRC}
                    />
                  </div>
                  <div className="nr-curso-text">
                    <span className="nr-curso-title">{c.titulo}</span>
                    {descSnippet ? (
                      <span className="nr-curso-desc">{descSnippet}</span>
                    ) : null}
                    <span className="nr-curso-price">{getCourseCheckoutPrice(c).toFixed(2)} €</span>
                  </div>
                </label>
              );
              })}
            </div>
          </div>

          <ConsentCheckbox
            checked={form.consentAccepted}
            onChange={(v) => onChange("consentAccepted", v)}
            error={submittedOnce && !form.consentAccepted ? "Debes aceptar los términos y la política de privacidad" : ""}
          />

          {/* Cupón + Botón en la misma fila */}
          <div className="nr-actions">
            <input
              className="nr-cupon"
              placeholder="Código de descuento"
              value={form.cuponCodigo}
              onChange={e=>onChange("cuponCodigo", e.target.value)}
            />
            <button
                type="submit"
                className="nr-submit"
                disabled={sending}
              >
                {sending ? "Enviando…" : "Continuar al pago"}
              </button>

          </div>
        </form>
      </div>
      <FooterPublico />
    </div>
  );
}
